import React, { useState, useEffect, useCallback } from 'react';
import EmojiPicker from 'emoji-picker-react';
import SquarePayment from './SquarePayment';
import About from './About';
import Contact from './Contact';
import axios from 'axios';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || '';

// US States and Territories supported by Lob
const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'AA', name: 'Armed Forces Americas' },
  { code: 'AE', name: 'Armed Forces Europe' },
  { code: 'AP', name: 'Armed Forces Pacific' },
  { code: 'AS', name: 'American Samoa' },
  { code: 'GU', name: 'Guam' },
  { code: 'MP', name: 'Northern Mariana Islands' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'VI', name: 'U.S. Virgin Islands' }
];

// Convert emoji to Twemoji SVG URL
const emojiToTwemojiUrl = (emoji) => {
  if (!emoji) return '';

  // Convert emoji to Unicode codepoints, filtering out variation selectors
  const codepoints = [];
  for (const char of emoji) {
    const codepoint = char.codePointAt(0);
    // Filter out variation selectors (U+FE0F and U+FE0E)
    // These force emoji/text presentation but Twemoji CDN doesn't include them in paths
    if (codepoint !== 0xFE0F && codepoint !== 0xFE0E) {
      codepoints.push(codepoint.toString(16));
    }
  }

  const codepointString = codepoints.join('-');
  return `https://cdn.jsdelivr.net/gh/jdecked/twemoji@latest/assets/svg/${codepointString}.svg`;
};

function App() {
  // Navigation
  const [currentPage, setCurrentPage] = useState('home'); // 'home', 'about', 'contact'

  // Square configuration
  const [squareAppId, setSquareAppId] = useState(null);
  const [squareLocationId, setSquareLocationId] = useState(null);
  const [squareScriptLoaded, setSquareScriptLoaded] = useState(false);
  const [squareEnvironment, setSquareEnvironment] = useState(null);

  // Pricing
  const [price, setPrice] = useState(null); // Will be fetched from API
  const [discountCode, setDiscountCode] = useState(null); // Store discount code if valid

  // Step 1: Emoji selection
  const [selectedEmoji, setSelectedEmoji] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  // Step 2: Message
  const [message, setMessage] = useState('');

  // Step 2.5: Preview
  const [showPreview, setShowPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewImages, setPreviewImages] = useState({ front: null, back: null });
  const [previewError, setPreviewError] = useState('');

  // Step 3: Address
  const [address, setAddress] = useState({
    name: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
  });

  // Buyer email as separate state
  const [buyerEmail, setBuyerEmail] = useState('');

  const [addressValidation, setAddressValidation] = useState({
    status: 'idle', // idle, validating, valid, invalid
    message: '',
  });

  // Step 5: Payment & Order
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderStatus, setOrderStatus] = useState(null); // null, success, error
  const [orderId, setOrderId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [receiptUrl, setReceiptUrl] = useState(null);

  // Success screen thumbnails
  const [successThumbnails, setSuccessThumbnails] = useState(null);
  const [thumbnailsLoading, setThumbnailsLoading] = useState(false);
  const [thumbnailsFailed, setThumbnailsFailed] = useState(false);

  // Fetch Square config and dynamically load Square.js on mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const configResponse = await axios.get(`${API_URL}/api/config`);
        setSquareAppId(configResponse.data.square_application_id);
        setSquareLocationId(configResponse.data.square_location_id);

        // Dynamically load the correct Square.js script based on environment
        const squareEnvironment = configResponse.data.square_environment;
        setSquareEnvironment(squareEnvironment);
        const scriptUrl = squareEnvironment === 'sandbox'
          ? 'https://sandbox.web.squarecdn.com/v1/square.js'
          : 'https://web.squarecdn.com/v1/square.js';

        // Check if script already exists
        const existingScript = document.querySelector(`script[src*="square.js"]`);
        if (existingScript) {
          existingScript.remove();
        }

        // Load the appropriate Square.js script
        const script = document.createElement('script');
        script.src = scriptUrl;
        script.type = 'text/javascript';
        script.onload = () => {
          console.log(`Square.js loaded (${squareEnvironment})`);
          setSquareScriptLoaded(true);
        };
        script.onerror = () => {
          console.error('Failed to load Square.js');
        };
        document.head.appendChild(script);

        console.log('Square config loaded');

        // Pre-populate address, emoji, and message with test data in staging (sandbox) environment
        if (squareEnvironment === 'sandbox') {
          setAddress({
            name: 'Test User',
            address1: 'deliverable',
            address2: '',
            city: 'San Francisco',
            state: 'CA',
            zip: '11111',
          });
          setSelectedEmoji('😊');
          setMessage('Hello from staging! This is a test message.');
        }
      } catch (error) {
        console.error('Failed to fetch Square config:', error);
      }
    };
    fetchConfig();
  }, []);

  // Check for discount code in URL and fetch appropriate price
  useEffect(() => {
    const checkDiscountAndFetchPrice = async () => {
      // Check URL path for discount code (e.g., /discount)
      const path = window.location.pathname;
      const pathSegments = path.split('/').filter(segment => segment);

      if (pathSegments.length > 0) {
        const potentialCode = pathSegments[0];

        // Track the visit first (fire and forget)
        try {
          await axios.post(`${API_URL}/api/track-coupon-visit/${potentialCode}`);
          console.log('Coupon visit tracked:', potentialCode);
        } catch (error) {
          // Silently fail if tracking doesn't work
          console.log('Visit tracking failed (non-critical)');
        }

        // Try to validate discount code
        try {
          const discountResponse = await axios.get(`${API_URL}/api/validate-discount/${potentialCode}`);
          if (discountResponse.data.valid) {
            setDiscountCode(potentialCode);
            setPrice(discountResponse.data.price / 100); // Use discounted price
            console.log('Discount code applied:', potentialCode);
            return;
          }
        } catch (error) {
          // Discount not valid or not enabled, fall through to regular price
          console.log('No valid discount code in URL');
        }
      }

      // Fetch regular price if no discount
      try {
        const response = await axios.get(`${API_URL}/api/price`);
        setPrice(response.data.price / 100); // Convert cents to dollars
      } catch (error) {
        console.error('Failed to fetch price, using default:', error);
        // Keep default $5.00 if API fails
      }
    };
    checkDiscountAndFetchPrice();
  }, []);

  const handleEmojiClick = (emojiData) => {
    setSelectedEmoji(emojiData.emoji);
    setShowEmojiPicker(false);
    // Reset preview when emoji changes
    setShowPreview(false);
    setPreviewImages({ front: null, back: null });

    // Track emoji selection in GA4
    if (window.gtag) {
      window.gtag('event', 'select_emoji', {
        emoji: emojiData.emoji,
      });
    }
  };

  const checkImageAvailable = async (url, maxAttempts = 15) => {
    // Poll the image URL until it's available or max attempts reached
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        // Use Image object to check if image loads (bypasses CORS issues with HEAD)
        await new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(true);
          img.onerror = () => reject(new Error('Image failed to load'));
          img.src = url;
        });
        return true;
      } catch (error) {
        // Image not ready yet
      }
      // Wait 2 seconds before next attempt
      await new Promise(resolve => setTimeout(resolve, 2000));
    }
    return false;
  };

  const generatePreview = async () => {
    if (!selectedEmoji) return;

    setPreviewLoading(true);
    setPreviewError('');

    try {
      const response = await axios.post(`${API_URL}/api/preview`, {
        emoji: selectedEmoji,
        message: message || null,
        recipient_name: address.name || null,
        recipient_address1: address.address1 || null,
        recipient_address2: address.address2 || null,
        recipient_city: address.city || null,
        recipient_state: address.state || null,
        recipient_zip: address.zip || null,
        recipient_country: 'US',
      });

      const frontUrl = response.data.front_url;
      const backUrl = response.data.back_url;

      // Check if images are available (Lob may take time to render them)
      const [frontAvailable, backAvailable] = await Promise.all([
        checkImageAvailable(frontUrl),
        checkImageAvailable(backUrl),
      ]);

      if (frontAvailable && backAvailable) {
        setPreviewImages({
          front: frontUrl,
          back: backUrl,
        });
        setShowPreview(true);

        // Track preview generation in GA4
        if (window.gtag) {
          window.gtag('event', 'view_preview', {
            has_message: !!message,
          });
        }
      } else {
        setPreviewError(
          'Preview images are taking longer than expected to generate. Please try again.'
        );
      }
    } catch (error) {
      setPreviewError(
        error.response?.data?.detail || 'Failed to generate preview. Please try again.'
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleMessageChange = (value) => {
    setMessage(value);
    // Reset preview when message changes
    if (showPreview) {
      setShowPreview(false);
      setPreviewImages({ front: null, back: null });
    }
  };

  const handleAddressChange = (field, value) => {
    setAddress({ ...address, [field]: value });
    setAddressValidation({ status: 'idle', message: '' });
  };

  const validateAddress = async () => {
    if (!address.name || !address.address1 || !address.city || !address.state || !address.zip) {
      return;
    }

    setAddressValidation({ status: 'validating', message: 'Validating address...' });

    try {
      const response = await axios.post(`${API_URL}/api/validate-address`, {
        name: address.name,
        address1: address.address1,
        address2: address.address2,
        city: address.city,
        state: address.state,
        zip_code: address.zip,
        country: 'US',
      });

      if (response.data.valid) {
        setAddressValidation({ status: 'valid', message: 'Address is valid!' });
        // Update with validated address
        if (response.data.address) {
          setAddress({
            name: response.data.address.name,
            address1: response.data.address.address1,
            address2: response.data.address.address2,
            city: response.data.address.city,
            state: response.data.address.state,
            zip: response.data.address.zip,
          });
        }

        // Track address validation in GA4
        if (window.gtag) {
          window.gtag('event', 'add_shipping_info', {
            shipping_tier: 'standard',
          });
        }
      } else {
        setAddressValidation({
          status: 'invalid',
          message: response.data.error || 'Address could not be validated',
        });
      }
    } catch (error) {
      setAddressValidation({
        status: 'invalid',
        message: 'Error validating address. Please check your input.',
      });
    }
  };

  const fetchSuccessThumbnails = async (orderId) => {
    console.log(`[THUMBNAILS] Starting to fetch thumbnails for order ${orderId}`);
    setThumbnailsLoading(true);
    setThumbnailsFailed(false);

    // Poll backend endpoint (which returns cached thumbnails from database)
    // Poll every 2 seconds for up to 90 seconds (45 attempts)
    for (let i = 0; i < 45; i++) {
      try {
        console.log(`[THUMBNAILS] Polling attempt ${i + 1}/45 for order ${orderId}`);
        const response = await axios.get(`${API_URL}/api/order/${orderId}/thumbnails`, {
          validateStatus: (status) => status === 200 || status === 202 // Accept both 200 and 202
        });

        console.log(`[THUMBNAILS] Response status: ${response.status}`);

        // Check if response has the actual thumbnail data (status 200)
        if (response.status === 200 && response.data.front && response.data.back) {
          console.log(`[THUMBNAILS] Successfully received thumbnails for order ${orderId}`);
          setSuccessThumbnails(response.data);
          setThumbnailsLoading(false);
          return;
        } else if (response.status === 202 || !response.data.front || !response.data.back) {
          // 202 or missing thumbnail data = not ready yet, keep polling
          console.log(`[THUMBNAILS] Attempt ${i + 1}/45: Thumbnails not ready yet (status ${response.status}), waiting 2 seconds...`);
          await new Promise(resolve => setTimeout(resolve, 2000));
          continue;
        }
      } catch (error) {
        // Any other error, give up
        console.error(`[THUMBNAILS] Failed to fetch thumbnails after ${i + 1} attempts:`, error);
        break;
      }
    }

    console.log(`[THUMBNAILS] Gave up fetching thumbnails after 45 attempts`);
    setThumbnailsLoading(false);
    setThumbnailsFailed(true);
  };

  const pollOrderStatus = async (orderId, maxAttempts = 30) => {
    // Poll every 2 seconds, up to 60 seconds total
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const response = await axios.get(`${API_URL}/api/order/${orderId}`);
        const status = response.data.status;

        console.log(`Order ${orderId} status: ${status} (attempt ${attempt + 1}/${maxAttempts})`);

        if (status === 'completed') {
          // Payment processed and postcard sent successfully
          setOrderStatus('success');
          setIsProcessing(false);

          // Save receipt URL
          if (response.data.receipt_url) {
            setReceiptUrl(response.data.receipt_url);
          }

          // Start fetching thumbnails
          fetchSuccessThumbnails(orderId);

          // Track successful purchase in GA4
          if (window.gtag) {
            window.gtag('event', 'purchase', {
              transaction_id: orderId,
              value: price,
              currency: 'USD',
              items: [{
                item_id: 'postcard',
                item_name: 'Emoji Postcard',
                price: price,
                quantity: 1,
              }],
            });
          }
          return;
        } else if (status === 'failed') {
          // Payment or postcard processing failed
          setOrderStatus('error');
          setErrorMessage('Payment processing failed. Please try again or contact support.');
          setIsProcessing(false);
          return;
        }
        // Status is still 'pending' or 'processing', continue polling
        await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds
      } catch (error) {
        console.error('Error polling order status:', error);
        // Continue polling even if one request fails
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }

    // Timeout - order is still processing after max attempts
    setOrderStatus('error');
    setErrorMessage('Payment processing is taking longer than expected. Please check your order status later or contact support.');
    setIsProcessing(false);
  };

  const handlePaymentError = useCallback((error) => {
    setOrderStatus('error');
    setErrorMessage(error);
  }, []);

  const handlePayment = async (token) => {
    setIsProcessing(true);
    setErrorMessage('');
    setOrderStatus('processing');

    // Track payment initiation in GA4
    if (window.gtag) {
      window.gtag('event', 'begin_checkout', {
        currency: 'USD',
        value: price,
        items: [{
          item_id: 'postcard',
          item_name: 'Emoji Postcard',
          price: price,
          quantity: 1,
        }],
      });
    }

    try {
      const orderData = {
        emoji: selectedEmoji,
        message: message || null,
        recipient_name: address.name,
        recipient_address1: address.address1,
        recipient_address2: address.address2 || null,
        recipient_city: address.city,
        recipient_state: address.state,
        recipient_zip: address.zip,
        recipient_country: 'US',
        payment_token: token ? token.token : null,  // null for free orders
      };

      // Include buyer email if provided
      if (buyerEmail) {
        orderData.buyer_email = buyerEmail;
      }

      // Include discount code if present
      if (discountCode) {
        orderData.discount_code = discountCode;
      }

      const response = await axios.post(`${API_URL}/api/create-order`, orderData);

      const orderId = response.data.order_id;
      setOrderId(orderId);

      // Start polling for order status
      await pollOrderStatus(orderId);
    } catch (error) {
      setOrderStatus('error');
      setErrorMessage(
        error.response?.data?.detail || 'Failed to create order. Please try again.'
      );
      setIsProcessing(false);

      // Track payment failure in GA4
      if (window.gtag) {
        window.gtag('event', 'payment_failed', {
          error_message: error.response?.data?.detail || 'Unknown error',
        });
      }
    }
  };

  const isReadyForPayment = () => {
    return (
      selectedEmoji &&
      addressValidation.status === 'valid' &&
      !isProcessing &&
      price !== null &&  // Ensure price has been fetched
      (price === 0 || squareAppId)  // Allow submit if free OR Square configured
    );
  };

  // Navigation component
  const Navigation = () => {
    const handleNavigationClick = (page) => {
      setCurrentPage(page);

      // Track page views in GA4
      if (window.gtag) {
        window.gtag('event', 'page_view', {
          page_title: page === 'home' ? 'Home' : page === 'about' ? 'About' : 'Contact',
          page_location: window.location.href,
        });
      }
    };

    return (
      <nav className="navigation">
        <button onClick={() => handleNavigationClick('home')} className={currentPage === 'home' ? 'active' : ''}>
          Home
        </button>
        <button onClick={() => handleNavigationClick('about')} className={currentPage === 'about' ? 'active' : ''}>
          About
        </button>
        <button onClick={() => handleNavigationClick('contact')} className={currentPage === 'contact' ? 'active' : ''}>
          Contact
        </button>
      </nav>
    );
  };

  // Footer component
  const Footer = () => (
    <footer className="footer">
      <p>&copy; {new Date().getFullYear()} Emoji Postcard. All rights reserved.</p>
    </footer>
  );

  // Show About page
  if (currentPage === 'about') {
    return (
      <>
        <Navigation />
        <About />
        <Footer />
      </>
    );
  }

  // Show Contact page
  if (currentPage === 'contact') {
    return (
      <>
        <Navigation />
        <Contact />
        <Footer />
      </>
    );
  }

  // Show processing screen while waiting for payment confirmation
  if (orderStatus === 'processing') {
    return (
      <>
        <Navigation />
        <div className="App">
          <div className="header">
            <h1>
              <img
                src={emojiToTwemojiUrl('⏳')}
                alt="⏳"
                style={{ width: '32px', height: '32px', verticalAlign: 'middle', marginRight: '8px' }}
              />
              Processing...
            </h1>
          </div>
          <div className="card">
            <div className="success-message">
              <h2>Processing your payment</h2>
              <p>Please wait while we confirm your payment and prepare your postcard...</p>
              {orderId && (
                <div className="order-id" style={{ marginTop: '20px' }}>Order ID: {orderId}</div>
              )}
              <div style={{ marginTop: '30px', textAlign: 'center' }}>
                <div className="spinner-large"></div>
              </div>
              <p style={{ marginTop: '20px', fontSize: '0.9rem', color: '#666' }}>
                This usually takes just a few seconds.
              </p>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (orderStatus === 'success') {
    return (
      <>
        <Navigation />
        <div className="App">
          <div className="header">
            <h1>
              <img
                src={emojiToTwemojiUrl('🎉')}
                alt="🎉"
                style={{ width: '32px', height: '32px', verticalAlign: 'middle', marginRight: '8px' }}
              />
              Success!
            </h1>
          </div>
          <div className="card">
            <div className="success-message">
              <h2>Your postcard is on its way!</h2>
              <p>Payment processed successfully and your emoji postcard has been sent to the mail.</p>
              <div className="order-id">Order ID: {orderId}</div>

              {/* Receipt link */}
              {receiptUrl && (
                <div style={{ marginTop: '20px' }}>
                  <a
                    href={receiptUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="receipt-link"
                    style={{
                      display: 'inline-block',
                      padding: '10px 20px',
                      backgroundColor: '#f0f0f0',
                      color: '#333',
                      textDecoration: 'none',
                      borderRadius: '5px',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                    }}
                  >
                    View Payment Receipt
                  </a>
                </div>
              )}

              {/* Thumbnail preview */}
              <div style={{ marginTop: '25px' }}>
                {thumbnailsLoading ? (
                  <div className="thumbnail-loading">
                    <div className="spinner" style={{ margin: '20px auto' }}></div>
                    <p style={{ fontSize: '0.9rem', color: '#666' }}>Generating postcard preview...</p>
                  </div>
                ) : successThumbnails ? (
                  <div className="thumbnail-preview">
                    <h3 style={{ fontSize: '1.1rem', marginBottom: '15px' }}>Your Postcard Preview</h3>
                    <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
                      <div style={{ textAlign: 'center' }}>
                        <img
                          src={successThumbnails.front}
                          alt="Postcard front"
                          style={{ maxWidth: '250px', border: '2px solid #ddd', borderRadius: '8px' }}
                        />
                        <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '5px' }}>Front</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <img
                          src={successThumbnails.back}
                          alt="Postcard back"
                          style={{ maxWidth: '250px', border: '2px solid #ddd', borderRadius: '8px' }}
                        />
                        <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '5px' }}>Back</div>
                      </div>
                    </div>
                  </div>
                ) : thumbnailsFailed ? (
                  <div style={{
                    padding: '15px',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '5px',
                    fontSize: '0.9rem',
                    color: '#856404'
                  }}>
                    <p style={{ margin: '0 0 8px 0', fontWeight: '500' }}>Preview not available at this time</p>
                    <p style={{ margin: 0 }}>
                      Our third-party rendering service is experiencing issues. Rest assured, your postcard is on its way.
                    </p>
                  </div>
                ) : null}
              </div>

              <p style={{ marginTop: '25px', fontSize: '0.9rem', color: '#666' }}>
                Your postcard will arrive in 5-7 business days.
              </p>
            </div>
            <button
              className="submit-button"
              onClick={() => window.location.reload()}
              style={{ marginTop: '20px' }}
            >
              Send Another Postcard
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="App">
        <div className="header">
          <h1>
            <img
              src={emojiToTwemojiUrl('📮')}
              alt="📮"
              style={{ width: '32px', height: '32px', verticalAlign: 'middle', marginRight: '8px' }}
            />
            Emoji Postcard
          </h1>
          <p>Send emojis by mail - Choose an emoji, add a message, and send!</p>
        </div>

        {/* Mockup Image */}
        <div className="mockup-container">
          <img
            src={process.env.PUBLIC_URL + '/mockup.png'}
            alt="Emoji postcard mockup"
            className="mockup-image"
          />
        </div>

      <div className="card">
        {/* Step 1: Choose Emoji */}
        <div className="step-container">
          <div className="step-header">
            <div className="step-number">1</div>
            <h2 className="step-title">Choose Your Emoji</h2>
          </div>

          {selectedEmoji ? (
            <div className="emoji-display">
              <div className="selected-emoji">
                <img
                  src={emojiToTwemojiUrl(selectedEmoji)}
                  alt={selectedEmoji}
                  style={{ width: '80px', height: '80px' }}
                />
              </div>
              <button onClick={() => setShowEmojiPicker(!showEmojiPicker)}>
                {showEmojiPicker ? 'Close Picker' : 'Change Emoji'}
              </button>
            </div>
          ) : (
            <div className="emoji-display">
              <p style={{ marginBottom: '15px', color: '#666' }}>
                Select an emoji for the front of your postcard
              </p>
              <button onClick={() => setShowEmojiPicker(true)}>
                Choose Emoji
              </button>
            </div>
          )}

          {showEmojiPicker && (
            <div className="emoji-picker-container">
              <EmojiPicker
                onEmojiClick={handleEmojiClick}
                width="100%"
                height="400px"
                searchPlaceHolder="Search emoji..."
                emojiStyle="twitter"
              />
            </div>
          )}
        </div>

        {/* Step 2: Add Message */}
        <div className="step-container">
          <div className="step-header">
            <div className="step-number">2</div>
            <h2 className="step-title">Add a Message (Optional)</h2>
          </div>

          <div className="form-group">
            <textarea
              placeholder="Write a short message for the back of your postcard..."
              value={message}
              onChange={(e) => handleMessageChange(e.target.value.slice(0, 350))}
              maxLength={350}
            />
            <small>{message.length}/350 characters</small>
          </div>

          {selectedEmoji && !showPreview && (
            <button
              onClick={generatePreview}
              disabled={previewLoading}
              className="preview-button"
              style={{ marginTop: '15px' }}
            >
              {previewLoading ? (
                <span className="button-loading">
                  <span className="spinner"></span>
                  Generating Preview...
                </span>
              ) : (
                'Preview Postcard'
              )}
            </button>
          )}

          {previewError && (
            <div className="error-message" style={{ marginTop: '15px' }}>
              <p>{previewError}</p>
            </div>
          )}
        </div>

        {/* Step 2.5: Preview */}
        {showPreview && previewImages.front && previewImages.back && (
          <div className="step-container preview-container">
            <div className="step-header">
              <h2 className="step-title">📸 Preview Your Postcard</h2>
            </div>

            <div className="preview-images">
              <div className="preview-side">
                <h3>Front</h3>
                <img src={previewImages.front} alt="Postcard Front" />
              </div>
              <div className="preview-side">
                <h3>Back</h3>
                <img src={previewImages.back} alt="Postcard Back" />
              </div>
            </div>

            <p className="preview-disclaimer">
              Preview address shown will be updated with your recipient's information at checkout
            </p>

            <div style={{ textAlign: 'center', marginTop: '15px' }}>
              <button
                onClick={() => {
                  setShowPreview(false);
                  setPreviewImages({ front: null, back: null });
                }}
                className="secondary-button"
              >
                Make Changes
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Enter Address */}
        <div className="step-container">
          <div className="step-header">
            <div className="step-number">3</div>
            <h2 className="step-title">Recipient Address</h2>
          </div>

          <div className="form-group">
            <label>Recipient Name</label>
            <input
              type="text"
              placeholder="John Doe"
              value={address.name}
              onChange={(e) => handleAddressChange('name', e.target.value)}
              onBlur={validateAddress}
            />
          </div>

          <div className="form-group">
            <label>Street Address</label>
            <input
              type="text"
              placeholder="123 Main St"
              value={address.address1}
              onChange={(e) => handleAddressChange('address1', e.target.value)}
              onBlur={validateAddress}
            />
          </div>

          <div className="form-group">
            <label>Apartment, suite, etc. (optional)</label>
            <input
              type="text"
              placeholder="Apt 4B"
              value={address.address2}
              onChange={(e) => handleAddressChange('address2', e.target.value)}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>City</label>
              <input
                type="text"
                placeholder="San Francisco"
                value={address.city}
                onChange={(e) => handleAddressChange('city', e.target.value)}
                onBlur={validateAddress}
              />
            </div>

            <div className="form-group">
              <label>State</label>
              <select
                value={address.state}
                onChange={(e) => handleAddressChange('state', e.target.value)}
                onBlur={validateAddress}
              >
                <option value="">Select a state...</option>
                {US_STATES.map((state) => (
                  <option key={state.code} value={state.code}>
                    {state.name} ({state.code})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>ZIP Code</label>
            <input
              type="text"
              placeholder="94102"
              value={address.zip}
              onChange={(e) => handleAddressChange('zip', e.target.value)}
              onBlur={validateAddress}
              maxLength={10}
            />
          </div>

          {addressValidation.status !== 'idle' && (
            <div className={`validation-status ${addressValidation.status}`}>
              {addressValidation.message}
            </div>
          )}
        </div>

        {/* Step 4: Email for Receipt (Optional) */}
        <div className="step-container">
          <div className="step-header">
            <div className="step-number">4</div>
            <h2 className="step-title">Email for Receipt (Optional)</h2>
          </div>

          <div className="form-group">
            <label>Your Email</label>
            <input
              type="email"
              placeholder="you@example.com"
              value={buyerEmail}
              onChange={(e) => setBuyerEmail(e.target.value)}
            />
            <small style={{ color: '#666', fontSize: '0.85em', display: 'block', marginTop: '8px' }}>
              We'll send your payment receipt to this email. Leave blank if you don't want a receipt.
            </small>
          </div>
        </div>

        {/* Step 5: Payment */}
        <div className="step-container">
          <div className="step-header">
            <div className="step-number">5</div>
            <h2 className="step-title">Payment</h2>
          </div>

          <div className="price-display">
            <div className="amount">
              {price !== null ? `$${price.toFixed(2)}` : 'Loading...'}
            </div>
            <div className="description">Includes postage and handling</div>
          </div>

          {orderStatus === 'error' && (
            <div className="error-message">
              <h3>Payment Failed</h3>
              <p>{errorMessage}</p>
            </div>
          )}

          {!isReadyForPayment() && (
            <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
              {!selectedEmoji && <p>Please select an emoji</p>}
              {selectedEmoji && addressValidation.status !== 'valid' && (
                <p>Please enter and validate the recipient address</p>
              )}
            </div>
          )}

          {isReadyForPayment() && price === 0 && (
            <div className="payment-container">
              <button
                onClick={() => handlePayment(null)}
                className="submit-button"
                disabled={isProcessing}
                style={{
                  width: '100%',
                  padding: '15px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  backgroundColor: '#DD2E44',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isProcessing ? 'not-allowed' : 'pointer',
                  opacity: isProcessing ? 0.6 : 1
                }}
              >
                Send Free Postcard
              </button>
              <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '10px', textAlign: 'center' }}>
                No payment required with this coupon code
              </p>
            </div>
          )}

          {isReadyForPayment() && price > 0 && squareAppId && squareLocationId && squareScriptLoaded && (
            <div className="payment-container">
              <SquarePayment
                applicationId={squareAppId}
                locationId={squareLocationId}
                price={price}
                onPaymentSuccess={handlePayment}
                onPaymentError={handlePaymentError}
              />
              <div className="payment-footer-text">
                Your payment information is encrypted and securely processed by <a href="https://squareup.com/" target="_blank" rel="noopener noreferrer">Square</a>.
              </div>
            </div>
          )}

          {isReadyForPayment() && price > 0 && squareAppId && squareLocationId && !squareScriptLoaded && (
            <div className="loading">
              <div className="spinner"></div>
              <p>Loading payment form...</p>
            </div>
          )}

          {isReadyForPayment() && price > 0 && (!squareAppId || !squareLocationId) && (
            <div className="error-message">
              <p>Payment system is not configured. Please contact support.</p>
            </div>
          )}

          {isProcessing && (
            <div className="loading">
              Processing your order
            </div>
          )}
        </div>
      </div>
    </div>
    <Footer />
    </>
  );
}

export default App;
